import React, { useEffect, useState } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";

import Modal from "react-bootstrap/Modal";
import { useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";

import useHostFooterAction from "components/organisms/CreationModal/hooks/useHostFooterAction";

import SourceMappingArea from "../../components/SourceMappingArea";
import "../../css/createSourceModal.css";
import {
    bigqueryAuthorize,
    createSourceBigquery,
    getBigquerySettings,
    getBigqueryTables,
    getBigqueryUrl,
    postBigquerySettings,
} from "../../helpers/Sources/bigquery/bigquerySource";
import { usageSourceCode } from "../../helpers/Sources/createSource";
import LoginButton from "../molecules/LoginButton";
import { ConfirmationModal } from "../organisms/ConfirmationModal";
import SwalAlertComp from "../SwalAlert";

const toApiDatetime = (value) => {
    const match = value
        .trim()
        .match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) {
        return value;
    }
    const [, year, month, day, hours, minutes, seconds = "00"] = match;
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const BigquerySource = ({
    handleClose,
    setBigqueryStep,
    setNextCheck,
    handleGetSources,
    addToTracker,
    getAllSourcesReduxProps,
    selectFileSourceId,
    getTracker,
    isModal,
    useHostFooter = false,
    registerFooterAction,
}) => {
    const language = useSelector((state) => state.languagesState.languages);

    const [isLoadingButton, setIsLoadingButton] = useState(false);
    const [bigqueryName, setBigqueryName] = useState("");
    const [bigqueryProgressStep, setProgressStep] = useState(0);
    const [bigqueryInstallId, setBigqueryInstallId] = useState();
    const [bigqueryProjects, setBigqueryProjects] = useState();
    const [bigqueryTables, setBigqueryTables] = useState();
    const [bigqueryDatasets, setBigqueryDatasets] = useState();
    const [bigquerySelectedProjectId, setBigquerySelectedProjectId] =
        useState();
    const [bigquerySelectedDataset, setBigquerySelectedDataset] = useState();
    const [bigquerySelectedTable, setBigquerySelectedTable] = useState();
    const [bigqueryUsageList, setBigqueryUsageList] = useState();
    const [bigquerySeletectedUsage, setBigquerySeletectedUsage] = useState([]);
    const [bigquerySourceID, setBigquerySourceID] = useState();
    const [bigquerySourceTypeID, setBigquerySourceTypeID] = useState();
    const [bigquerySchedules, setBigquerySchedules] = useState([]);
    const [bigqueryPeriods, setBigqueryPeriods] = useState([]);

    const [selectedBigquerySchedule, setSelectedBigquerySchedule] =
        useState(null);
    const [selectedBigqueryPeriod, setSelectedBigqueryPeriod] = useState(null);
    const [scheduleStartDatetime, setScheduleStartDatetime] = useState("");

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState("");
    const [isConfirmLoading, setIsConfirmLoading] = useState(false);

    const initCRM = async () => {
        let usage_list = await usageSourceCode("spreadsheet");
        setBigqueryUsageList(
            usage_list?.data.map((item) => ({
                value: item?.code,
                label: item?.name,
                id: item?.id,
            })),
        );
        setBigquerySeletectedUsage(["offline"]);
    };

    const fetchSettings = async () => {
        const { status, data } = await getBigquerySettings();
        if (status) {
            const schedules = data?.schedules;
            const periods = data?.reading;

            setBigquerySchedules(
                Object.keys(schedules)?.map((scheduleKey) => ({
                    label: schedules[scheduleKey],
                    value: scheduleKey,
                })),
            );
            setBigqueryPeriods(
                Object.keys(periods)?.map((periodKey) => ({
                    label: periods[periodKey],
                    value: periodKey,
                })),
            );
        }
    };

    useEffect(() => {
        initCRM();
        fetchSettings();
    }, []);

    const sendBigqueryData = async (data) => {
        setIsLoadingButton(true);

        let newData = {
            code: data?.code,
        };
        let response = await bigqueryAuthorize(newData);
        if (response.status) {
            setBigqueryInstallId(response?.data?.install_id);
            setBigqueryProjects(response?.data?.projects);
            setProgressStep(1);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response?.message ?? "",
                icon: "error",
            });
        }
        setIsLoadingButton(false);
    };

    useEffect(() => {
        setBigqueryStep && setBigqueryStep(bigqueryProgressStep);
    }, [bigqueryProgressStep]);

    const handleProjectIdChange = (event, selectedOpt) => {
        setBigquerySelectedProjectId(selectedOpt.project_id);
        let datasets = [...bigqueryProjects].find(
            (x) => x.project_id === selectedOpt.project_id,
        ).datasets;
        setBigqueryDatasets(datasets.map((x) => ({ value: x, label: x })));
    };

    const handleDatasetChange = (event, selectedOpt) => {
        setBigquerySelectedDataset(selectedOpt?.value);
    };

    const handleTableChange = (event, selectedOpt) => {
        setBigquerySelectedTable(selectedOpt?.value);
    };

    const handleNameChange = (event) => {
        setBigqueryName(event.target.value);
    };

    const handleUsageSelection = (event) => {
        setBigquerySeletectedUsage(event.target.value);
    };

    const nextStep2 = async () => {
        setIsLoadingButton(true);
        let response = await getBigqueryTables(
            bigquerySelectedProjectId,
            bigquerySelectedDataset,
            bigqueryInstallId,
        );
        if (response.status) {
            setBigqueryTables(
                response?.data?.tables.map((x) => ({
                    value: x,
                    label: x,
                })),
            );
            setProgressStep(2);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response?.message ?? "",
                icon: "error",
            });
        }
        setIsLoadingButton(false);
    };

    const finishSourceCreation = async (response) => {
        const datetimeForApi = scheduleStartDatetime
            ? toApiDatetime(scheduleStartDatetime)
            : null;
        await postBigquerySettings({
            schedule: selectedBigquerySchedule,
            reading: selectedBigqueryPeriod,
            source_id: response?.data?.source?.id,
            schedule_start_datetime: datetimeForApi,
        });
        setBigquerySourceID(response?.data?.source?.id);
        setBigquerySourceTypeID(response?.data?.source?.source_type_id);
        setProgressStep(3);
    };

    const submitBigquerySource = async (confirmed = false) => {
        let data = {
            name: bigqueryName,
            install_id: bigqueryInstallId,
            project: bigquerySelectedProjectId,
            dataset: bigquerySelectedDataset,
            table: bigquerySelectedTable,
            usage_types: [...bigqueryUsageList]
                .filter(
                    (x) =>
                        bigquerySeletectedUsage.filter((y) => y === x.value)
                            .length > 0,
                )
                .map((item) => item?.id),
            ...(confirmed ? { confirmed: true } : {}),
        };
        let response = await createSourceBigquery(data);
        if (!response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response?.message ?? "",
                icon: "error",
            });
            return { ok: false };
        }
        return { ok: true, response };
    };

    const nextStep3 = async () => {
        setIsLoadingButton(true);

        const { ok, response } = await submitBigquerySource(false);
        if (ok) {
            const confirmationData = response?.data;
            if (confirmationData?.requires_confirmation === true) {
                setConfirmMessage(
                    confirmationData?.message ??
                        "The selected table contains more than 5 million rows. Connecting this dataset may increase processing time and cost. Are you sure you want to continue?",
                );
                setConfirmOpen(true);
                setIsLoadingButton(false);
                return;
            }
            await finishSourceCreation(response);
        }
        setIsLoadingButton(false);
    };

    const handleConfirmCancel = () => {
        if (isConfirmLoading) return;
        setConfirmOpen(false);
        setConfirmMessage("");
    };

    const handleConfirmContinue = async () => {
        setIsConfirmLoading(true);
        const { ok, response } = await submitBigquerySource(true);
        setConfirmOpen(false);
        setConfirmMessage("");
        setIsConfirmLoading(false);
        if (ok) {
            await finishSourceCreation(response);
        }
    };

    // Steps 1 (dataset -> Next) and 2 (table/schedule -> Save) are driven from
    // the modal footer; step 0 keeps its OAuth button in the content and
    // step 3+ hands the Save over to SourceMappingArea.
    const isSaveStep = bigqueryProgressStep === 2;
    const isNextStep = bigqueryProgressStep === 1;
    const isSaveStepIncomplete =
        !bigquerySelectedTable ||
        !selectedBigqueryPeriod ||
        !selectedBigquerySchedule;

    useHostFooterAction(registerFooterAction, "form", {
        label: isSaveStep
            ? (language?.buttons?.save ?? "Save")
            : (language?.buttons?.next ?? "Next"),
        onSubmit: isSaveStep ? nextStep3 : nextStep2,
        isDisabled: isSaveStep
            ? isSaveStepIncomplete
            : !bigquerySelectedDataset,
        isLoading: isLoadingButton,
        hidden: !isNextStep && !isSaveStep,
    });

    const mappingSuccess = (res) => {
        SwalAlertComp({
            title: language?.delete_swal_modal?.success ?? "Success",
            message: res.message && res.message,
            icon: "success",
            timer: 3000,
        });
        handleClose(bigquerySourceID);
        if (setNextCheck) {
            setNextCheck(true);
        }
        if (handleGetSources) {
            handleGetSources();
        }
        if (getTracker) {
            addToTracker(bigquerySourceID);
        }
        getAllSourcesReduxProps && getAllSourcesReduxProps();

        setBigqueryName("");
        setProgressStep(0);
        if (selectFileSourceId) {
            selectFileSourceId(bigquerySourceID, bigquerySourceTypeID);
        }
    };

    return (
        <div>
            <ConfirmationModal
                open={confirmOpen}
                onClose={handleConfirmCancel}
                onConfirm={handleConfirmContinue}
                title={
                    language?.bigquery_large_dataset?.title ??
                    "Large Dataset Detected"
                }
                message={confirmMessage}
                confirmText={language?.buttons?.continue ?? "Continue"}
                cancelText={language?.delete_swal_modal?.no ?? "Cancel"}
                confirmColor="warning"
                isLoading={isConfirmLoading}
            />
            {bigqueryProgressStep === 0 && (
                <div>
                    <FormControl className="mb-3" style={{ width: "100%" }}>
                        <Select
                            name="usage"
                            label={language?.source_modal?.usage ?? "Usage"}
                            onChange={handleUsageSelection}
                            value={bigquerySeletectedUsage}
                            multiple
                        >
                            {bigqueryUsageList &&
                                bigqueryUsageList.map((item) => (
                                    <MenuItem
                                        key={item.value}
                                        value={item.value}
                                    >
                                        {item.label}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <FormControl className="mb-3" style={{ width: "100%" }}>
                        <TextField
                            name="name"
                            label={language?.source_modal?.name ?? "Name"}
                            variant="outlined"
                            onChange={handleNameChange}
                            placeholder={language?.source_modal?.name ?? "Name"}
                            value={bigqueryName}
                        />
                    </FormControl>

                    <LoginButton
                        code="bigquery"
                        getAuthUrl={getBigqueryUrl}
                        getAuthUrlResponseTransform={(response) => {
                            return response?.data?.auth_url;
                        }}
                        paramNames={["code"]}
                        onSuccess={sendBigqueryData}
                        dataTransform={(params) => ({ code: params.code })}
                        logoSrc="/assets/images/bigquery.png"
                        buttonText={
                            language?.buttons?.connect_with_bigquery ??
                            "Connect with Google BigQuery"
                        }
                        isLoadingExternal={isLoadingButton}
                        disabled={
                            !(
                                bigquerySeletectedUsage &&
                                bigquerySeletectedUsage.length > 0 &&
                                bigqueryName !== ""
                            )
                        }
                    />
                </div>
            )}
            {bigqueryProgressStep === 1 && (
                <div>
                    <FormControl className="mb-3" style={{ width: "100%" }}>
                        <Autocomplete
                            name="project_id"
                            label={
                                language?.source_modal?.project_id ??
                                "Project ID"
                            }
                            onChange={handleProjectIdChange}
                            value={bigquerySelectedProjectId}
                            options={bigqueryProjects && bigqueryProjects}
                            getOptionLabel={(option) => option.project_name}
                            freeSolo
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={
                                        language?.source_modal?.project_id ??
                                        "Project ID"
                                    }
                                />
                            )}
                        ></Autocomplete>
                    </FormControl>

                    {bigqueryDatasets && Array.isArray(bigqueryDatasets) && (
                        <div className="mt-3">
                            <FormControl
                                className="mb-3"
                                style={{ width: "100%" }}
                            >
                                <Autocomplete
                                    name="dataset"
                                    label={
                                        language?.source_modal?.dataset ??
                                        "Dataset"
                                    }
                                    onChange={handleDatasetChange}
                                    value={bigquerySelectedDataset}
                                    options={
                                        bigqueryDatasets && bigqueryDatasets
                                    }
                                    getOptionLabel={(option) =>
                                        option.label || ""
                                    }
                                    freeSolo
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={
                                                language?.source_modal
                                                    ?.dataset ?? "Dataset"
                                            }
                                        />
                                    )}
                                ></Autocomplete>
                            </FormControl>
                        </div>
                    )}

                    {!useHostFooter &&
                        bigquerySelectedDataset &&
                        bigquerySelectedDataset !== "" && (
                            <div>
                                <Modal.Footer className="source-modal-footer p-0 pt-3">
                                    <div className="position-relative">
                                        {isLoadingButton && (
                                            <div className="circle-loader max-height-45">
                                                <ClipLoader
                                                    color={"#000"}
                                                    size={17}
                                                />
                                            </div>
                                        )}
                                        <Button
                                            variant="contained"
                                            className="tracker-save-btn modal-save-btn"
                                            color="primary"
                                            onClick={nextStep2}
                                        >
                                            {language?.buttons?.next ?? "Next"}
                                        </Button>
                                    </div>
                                </Modal.Footer>
                            </div>
                        )}
                </div>
            )}
            {bigqueryProgressStep === 2 && (
                <div>
                    <FormControl className="mb-3" style={{ width: "100%" }}>
                        <Autocomplete
                            name="table"
                            label={language?.source_modal?.table ?? "Table"}
                            onChange={handleTableChange}
                            value={bigquerySelectedTable}
                            options={bigqueryTables && bigqueryTables}
                            getOptionLabel={(option) => option.label || ""}
                            freeSolo
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={
                                        language?.source_modal?.table ?? "Table"
                                    }
                                />
                            )}
                        ></Autocomplete>
                    </FormControl>
                    <FormControl fullWidth>
                        <Select
                            onChange={({ target: { value } }) =>
                                setSelectedBigquerySchedule(value)
                            }
                            value={selectedBigquerySchedule}
                            label="Data Update Period"
                            className="mb-3"
                            fullWidth
                        >
                            {bigquerySchedules?.map((option) => (
                                <MenuItem value={option?.value}>
                                    {option?.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <Select
                            onChange={({ target: { value } }) =>
                                setSelectedBigqueryPeriod(value)
                            }
                            value={selectedBigqueryPeriod}
                            label="Data Update Method"
                            className="mb-3"
                            fullWidth
                        >
                            {bigqueryPeriods?.map((option) => (
                                <MenuItem value={option?.value}>
                                    {option?.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth className="mb-3">
                        <TextField
                            label="Schedule Date & Time"
                            type="datetime-local"
                            value={scheduleStartDatetime}
                            onChange={(e) =>
                                setScheduleStartDatetime(e.target.value)
                            }
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </FormControl>
                    {!useHostFooter &&
                        bigquerySelectedTable &&
                        bigquerySelectedTable !== "" && (
                            <div>
                                <Modal.Footer className="source-modal-footer p-0 pt-3">
                                    <div className="position-relative">
                                        {isLoadingButton && (
                                            <div className="circle-loader max-height-45">
                                                <ClipLoader
                                                    color={"#000"}
                                                    size={17}
                                                />
                                            </div>
                                        )}
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            className="tracker-save-btn modal-save-btn"
                                            onClick={nextStep3}
                                            disabled={
                                                !bigquerySelectedTable ||
                                                !selectedBigqueryPeriod ||
                                                !selectedBigquerySchedule
                                            }
                                        >
                                            {language?.buttons?.save ?? "Save"}
                                        </Button>
                                    </div>
                                </Modal.Footer>
                            </div>
                        )}
                </div>
            )}
            {bigqueryProgressStep === 3 && (
                <div>
                    <SourceMappingArea
                        sourceId={bigquerySourceID}
                        sourceType={"bigquery"}
                        mappingSuccess={mappingSuccess}
                        isModal={isModal}
                        hideSaveButton={useHostFooter}
                        registerFooterAction={registerFooterAction}
                    ></SourceMappingArea>
                </div>
            )}
            {bigqueryProgressStep === 4 && (
                <div>
                    <SourceMappingArea
                        sourceId={bigquerySourceID}
                        sourceType={"bigquery"}
                        mappingSuccess={mappingSuccess}
                        isModal={isModal}
                        hideSaveButton={useHostFooter}
                        registerFooterAction={registerFooterAction}
                    ></SourceMappingArea>
                </div>
            )}
        </div>
    );
};

export default BigquerySource;
